import { useEffect, useState } from "react"

import { ContentObjectItem, ProjectConfiguration, SupportedEmbeddingTypes, VectorSearchQuery } from "@vertesia/common"
import { FilterX, X, FileCheck } from "lucide-react"
import { toast } from "sonner"

import { ActionTooltipButton } from "@components/buttons/ActionTooltipButton"
import { DocumentDataTable } from "@components/datatables/DocumentDataTable"
import { FiltersPanel } from "@components/filters/FiltersPanel"

import { transformFacetStatusResponses, transformFacetTypeResponses } from "@lib/facet"

import { FacetResponse } from "@models/facets"

import { useWatchDocumentSearchFacets, useWatchDocumentSearchResult } from "@plugins/providers/document/DocumentSearchContext"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"

interface SearchFilters {
    [key: string]: any
}

interface DocumentSelectionSearchPanelProps {
    information?: string
    children?: React.ReactNode
    multipleSelection?: ContentObjectItem[]
    onSelection?: (document: ContentObjectItem) => void
    onMultipleSelection?: (documents: ContentObjectItem[]) => void
}
export function DocumentSelectionSearchPanel({ information, children, multipleSelection, onSelection, onMultipleSelection }: Readonly<DocumentSelectionSearchPanelProps>) {
    const { hasMore, search, isSearching, error, objects } = useWatchDocumentSearchResult()
    const facets = useWatchDocumentSearchFacets()

    const { client, project, typeRegistry } = useUserSession()

    const [config, setConfig] = useState<ProjectConfiguration | undefined>(undefined)
    const embeddingsReady = !!project && (!!config?.embeddings.text || !!config?.embeddings.image)

    const [filters, setFilters] = useState<SearchFilters>({})
    const [statuses, setStatuses] = useState<FacetResponse[]>([])
    const [types, setTypes] = useState<FacetResponse[]>([])
    const [users, setUsers] = useState<FacetResponse[]>([])

    const [isReady, setIsReady] = useState(true)

    const [selectionDocuments, setSelectionDocuments] = useState<ContentObjectItem[]>(multipleSelection ?? [])

    const [clearFilters, setClearFilters] = useState(0)

    const generateTextEmbeddings = async (vectorText: string) => {
        if (!embeddingsReady || !vectorText || !config?.embeddings.text) {
            return
        }

        const response = await client.environments.embeddings(config.embeddings.text?.environment, { model: config.embeddings.text?.model, text: vectorText })

        const query: VectorSearchQuery = {
            values: response.values,
            config: {
                text: true,
                image: true,
                properties: true,
            }
        }

        return query
    }

    const searchObjects = async () => {
        const vectorText = filters?.vectorText?.value as string
        const vector: VectorSearchQuery | undefined = (vectorText ? await generateTextEmbeddings(vectorText) : undefined) as VectorSearchQuery

        const query: any = filters
            ? {
                createdFrom: filters?.date?.createdFrom,
                createdTo: filters?.date?.createdTo,
                updatedFrom: filters?.date?.updatedFrom,
                updatedTo: filters?.date?.updatedTo,
                full_text: filters?.property?.fullText,
                name: filters?.property?.name,
                status: filters?.status,
                type: filters?.type,
                vector,
                match: { created_by: filters?.user },
            }
            : {}

        search.query = query

        search.search()
    }

    const loadMoreDocuments = () => {
        setIsReady(false)

        search.loadMore().then(() => {
            setIsReady(true)
        })
    }

    const handleOnClearFilters = () => {
        setClearFilters(clearFilters + 1)
    }

    useEffect(() => {
        if (!client || !project) {
            return
        }

        client.projects.retrieve(project.id).then((project) => {
            setConfig(project.configuration)
        })
    }, [project, client])

    useEffect(() => {
        if (!error) {
            return
        }

        toast.error("Error while searching documents", {
            description: error.message,
            duration: 5000,
        })
    }, [error])

    useEffect(() => {
        setTypes(transformFacetTypeResponses(facets.type, typeRegistry))
        setStatuses(transformFacetStatusResponses(facets.status))
        setUsers(transformFacetStatusResponses((facets as any).created_by))
    }, [facets])

    useEffect(() => {
        searchObjects()
    }, [])

    const handleOnSelection = (document: ContentObjectItem) => {
        if (onSelection) {
            onSelection(document)
        }
        if (onMultipleSelection) {
            const isAlreadySelected = selectionDocuments.some((doc) => doc.id === document.id)

            if (isAlreadySelected) {
                setSelectionDocuments(selectionDocuments.filter((doc) => doc.id !== document.id))
            } else {
                setSelectionDocuments([...selectionDocuments, document])
            }
        }
    }

    const confirmMultipleSelection = () => {
        if (onMultipleSelection) {
            onMultipleSelection(selectionDocuments)
        }
    }

    return (
        <div className="flex h-full w-full">
            <FiltersPanel statuses={statuses} types={types} users={users} onFiltersChange={setFilters} onSearch={() => searchObjects()} onClearFilters={clearFilters} />
            <div className="w-full h-full flex flex-col gap-2">
                <div className="w-full flex flex-row gap-2 items-center justify-end p-2 pb-0">
                    {information && <div className="text-sm text-muted-foreground italic flex-1">{information}</div>}
                    <div className="flex h-10 gap-2">
                        {onMultipleSelection && selectionDocuments.length !== 0 && (
                            <div className="flex items-center gap-1">
                                <span className="text-sm text-gray-500">
                                    {selectionDocuments.length} document{selectionDocuments.length > 1 ? "s" : ""} selected
                                </span>
                                <ActionTooltipButton icon={<X />} tooltip="Clear Selection" variant="ghost" className="h-full text-gray-500" onClick={() => setSelectionDocuments([])} />
                                <Button variant="outline" onClick={confirmMultipleSelection}>
                                    <FileCheck />
                                    Confirm Selection
                                </Button>
                            </div>
                        )}
                        {filters && Object.keys(filters).length > 0 && <ActionTooltipButton icon={<FilterX />} tooltip="Clear Filters" variant="outline" className="h-full" onClick={handleOnClearFilters} />}
                        {children}
                    </div>
                </div>
                <div className="flex-grow w-full overflow-auto px-2">
                    <DocumentDataTable data={objects} loading={isSearching} hasMore={hasMore && isReady} loadMore={loadMoreDocuments} onSelection={handleOnSelection} selections={selectionDocuments} />
                </div>
            </div>
        </div>
    )
}
